import { skipToken } from "@reduxjs/toolkit/query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
	useGetDestinationTimestampsQuery,
	useGetReportStatusQuery,
} from "src/common/redux/api/exopenApi.js";
import { useGetAllIntegrationsQuery } from "src/common/service/fusionHub/fusionHubApi.js";
import { useGetIntegrationsQuery } from "src/common/service/nexus/nexusApi.js";
import { mergeDataSources } from "src/features/integrations/overview/table/mergeDataSources.js";
import { useDummyDisplayTypes } from "src/features/integrations/sources/dummy/dummyDisplayType.js";
import {
	useLoadedCompanyDomain,
	useLoadedLegalEntities,
} from "src/routes/routeDataHooks.js";
import { type StartSearchGroup, StartSearchView } from "./StartSearchView.js";
import { useOpenEwr } from "src/common/service/useEwrLinkClick.js";
import { useExcelReportFiles } from "src/features/excelReports/hooks/useExcelReportFiles.js";
import { useHasExcelReportAccess } from "src/features/excelReports/hooks/useHasExcelReportAccess.js";
import { getExcelTarget } from "src/features/products/products.js";
import { DOMAIN_TERMS } from "src/common/domainTerms.js";
import {
	getSourceMetadata,
	useSourceSystemMetadataMap,
} from "src/features/integrations/metadata.js";

const MAX_HITS_PER_GROUP = 5;

const matches = (query: string, ...haystacks: (string | null | undefined)[]) =>
	haystacks.some((haystack) =>
		haystack?.toLowerCase().includes(query.toLowerCase()),
	);

export const StartSearch = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const companyDomain = useLoadedCompanyDomain();
	const legalEntities = useLoadedLegalEntities();
	const { openEwr } = useOpenEwr();
	const metadataMap = useSourceSystemMetadataMap();

	// Same queries as the status widget — RTK Query dedupes, so no extra requests.
	const { data: fusionHubIntegrations, isLoading: isLoadingFusionHub } =
		useGetAllIntegrationsQuery({
			companyDomainId: companyDomain.id,
		});
	const { data: nexusIntegrations, isLoading: isLoadingNexus } =
		useGetIntegrationsQuery({
			companyDomainId: companyDomain.id,
		});
	const { data: destinationTimestamps } = useGetDestinationTimestampsQuery({
		companyDomainId: companyDomain.id,
	});
	const dummyDisplayTypes = useDummyDisplayTypes(fusionHubIntegrations?.items);
	const { data: reportStatus, isLoading: isLoadingReportStatus } =
		useGetReportStatusQuery({
			companyDomainId: companyDomain.id,
		});
	const hasExcelReportAccess = useHasExcelReportAccess(companyDomain.id);
	// WHY not useProductAccess: it would also fire the AI/planning access queries from the search box.
	const excelTarget = getExcelTarget(companyDomain, {
		hasExcelReportAccess,
		ewrUrl: reportStatus?.ewrUrl ?? null,
	});
	const reportPickerPath =
		excelTarget?.kind === "reportPicker" ? excelTarget.path : null;
	const ewrUrl = excelTarget?.kind === "ewr" ? excelTarget.url : null;
	// v7 users search the picker's reports; everyone else EWR's report modules.
	const { files: reportFiles } = useExcelReportFiles(
		reportPickerPath ? companyDomain.id : skipToken,
	);

	const [query, setQuery] = useState("");
	const trimmedQuery = query.trim();

	const groups: StartSearchGroup[] = useMemo(() => {
		if (!trimmedQuery) {
			return [];
		}

		const dataSources =
			fusionHubIntegrations && nexusIntegrations
				? mergeDataSources({
						legalEntities,
						fusionHubIntegrations: fusionHubIntegrations.items,
						nexusIntegrations,
						destinationTimestamps: destinationTimestamps ?? [],
						// Search lists names only; no sync status is rendered, so no jobs query.
						activeJobs: [],
						dummyDisplayTypes,
					})
				: [];

		const reports = reportPickerPath
			? reportFiles.map((file) => ({ id: String(file.id), label: file.name }))
			: ewrUrl
				? (reportStatus?.reportModules ?? []).map((reportModule) => ({
						id: reportModule.schema,
						label: reportModule.name,
					}))
				: [];

		return [
			{
				id: "legal-entities",
				label: t(DOMAIN_TERMS.COMPANIES),
				items: legalEntities
					.filter((legalEntity) => matches(trimmedQuery, legalEntity.name))
					.slice(0, MAX_HITS_PER_GROUP)
					.map((legalEntity) => ({
						id: legalEntity.id,
						label: legalEntity.name,
					})),
			},
			{
				id: "data-sources",
				label: t("Data sources"),
				items: dataSources
					.filter((dataSource) =>
						matches(
							trimmedQuery,
							dataSource.name,
							dataSource.sourceSystem,
							getSourceMetadata(metadataMap, dataSource.sourceSystem).name,
						),
					)
					.slice(0, MAX_HITS_PER_GROUP)
					.map((dataSource) => ({
						id: dataSource.id,
						label: dataSource.name,
						sublabel: dataSource.sourceSystem,
					})),
			},
			{
				id: "reports",
				label: t("Reports"),
				items: reports
					.filter((report) => matches(trimmedQuery, report.label))
					.slice(0, MAX_HITS_PER_GROUP),
			},
		];
	}, [
		trimmedQuery,
		legalEntities,
		reportStatus?.reportModules,
		ewrUrl,
		reportPickerPath,
		reportFiles,
		fusionHubIntegrations,
		nexusIntegrations,
		destinationTimestamps,
		dummyDisplayTypes,
		metadataMap,
		t,
	]);

	const handleSelect = (groupId: string, itemId: string) => {
		const label = groups
			.find((group) => group.id === groupId)
			?.items.find((item) => item.id === itemId)?.label;
		setQuery("");
		if (groupId === "legal-entities") {
			navigate(`/company-domains/${companyDomain.id}/legal-entities/${itemId}`);
		} else if (groupId === "data-sources") {
			// Match may be on source system, which the table's own search doesn't cover.
			navigate(`data-sources?q=${encodeURIComponent(label ?? trimmedQuery)}`);
		} else if (reportPickerPath) {
			// The picker opens the report, so its "didn't Excel open?" note stays in one place.
			navigate(
				`${reportPickerPath}?q=${encodeURIComponent(label ?? trimmedQuery)}`,
			);
		} else if (ewrUrl) {
			openEwr(ewrUrl);
		}
	};

	return (
		<StartSearchView
			query={query}
			onQueryChange={setQuery}
			groups={groups}
			// WHY no report files: a slow eox7 would block selecting any hit; the reports group fills in when they arrive.
			isLoading={isLoadingFusionHub || isLoadingNexus || isLoadingReportStatus}
			onSelect={handleSelect}
		/>
	);
};
